import { Box, Button, Flex, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { CarForm } from "../interfaces/CreateCarForm.types";

import { CurrencyInput, Dropdown, FormField } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { useCarMakes } from "@/services/cars/get-cars-models";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { Currency } from "@/types/currency";
import { formatDate } from "@/utils/formatDate";

import type { List } from "@/types/entity";
import type { Garage } from "@/types/garage";
import type { Dispatch, ElementType, FC, SetStateAction } from "react";

interface Props {
  garages: List<Garage> | undefined;
  setSelectedGarageForCarCreation?: Dispatch<SetStateAction<string | undefined>>;
  WrapperForFields: ElementType;
  wrapperProps: Record<string, unknown>;
  garageId: string | undefined;
  form: CarForm;
  showLessElements: boolean;
  handleMakeChange: (value?: string | null | undefined) => void;
  activeStep: number;
  onCancel: (() => void) | undefined;
  loading: boolean;
  nextStep: () => void;
  isStepValid: (step: number) => boolean;
  isEdit?: boolean;
}

const Step1: FC<Props> = ({
  garages,
  garageId,
  WrapperForFields,
  wrapperProps,
  setSelectedGarageForCarCreation,
  form,
  showLessElements,
  handleMakeChange,
  activeStep,
  onCancel,
  loading,
  nextStep,
  isStepValid,
  isEdit,
}) => {
  const { t } = useTranslation();
  const { data: plans } = usePlansUser();

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const numberOfCars = plans?.config.cars_per_garage;

  const { data: carMakes, isLoading: isLoadingMakes } = useCarMakes();

  const makeOptions =
    carMakes?.map((item) => ({
      value: item.make,
      label: item.make,
    })) || [];

  const showGarageSelector =
    (garages?.results?.length ?? 0) > 1 && setSelectedGarageForCarCreation !== undefined;

  return (
    <Stack gap="md">
      <Title order={4} mb="sm">
        {t("components.CreateCarForm.titles.basic")}
      </Title>

      {showGarageSelector && (
        <Dropdown
          label={t("dashboard.cars.select_garage")}
          placeholder={t("dashboard.cars.select_garage_placeholder")}
          data={(garages?.results || [])
            .filter((garage) =>
              typeof numberOfCars !== "number"
                ? true
                : (garage.data.belongs_to_car?.length ?? 0) < numberOfCars
            )
            .map((garage) => ({
              value: garage.identifier,
              label: garage.data.name,
            }))}
          value={garageId}
          onChange={(value) => setSelectedGarageForCarCreation?.(value || undefined)}
          required
          searchable
        />
      )}

      <WrapperForFields {...wrapperProps}>
        <TextInput
          label={t("car.carNickname")}
          placeholder={t("components.CreateCarForm.placeholders.nickname")}
          {...form.getInputProps("nickname")}
        />
        <FormField required>
          <TextInput
            label={t("car.owner")}
            placeholder={t("components.CreateCarForm.placeholders.owner")}
            {...form.getInputProps("owner")}
          />
        </FormField>
        {isEdit ? <Box>&nbsp;</Box> : null}
      </WrapperForFields>

      <WrapperForFields {...wrapperProps}>
        <FormField required>
          <CurrencyInput
            label={t("car.purchase_price")}
            value={(Number(form.values.purchase_price) || 0) * (Number(currency.value) || 1)}
            placeholder={t("components.CreateCarForm.placeholders.purchase_price", "Price")}
            currencySymbol={currency.symbol}
            onChange={(value) => {
              const adjustedValue = (Number(value) || 0) / (Number(currency.value) || 1);

              form.setFieldValue("purchase_price", adjustedValue);
              form.validateField("purchase_price");
            }}
          />
        </FormField>
        <FormField required>
          <DatePickerInput
            label={t("car.purchase_date")}
            placeholder={t("components.CreateCarForm.placeholders.purchase_date", "Select date")}
            valueFormat="YYYY-MM-DD"
            valueFormatter={(value) => formatDate(value.date as string)}
            {...form.getInputProps("purchase_date")}
          />
        </FormField>
      </WrapperForFields>

      <WrapperForFields {...wrapperProps}>
        <FormField required>
          <Dropdown
            label={t("car.make")}
            placeholder={t("components.CreateCarForm.placeholders.make")}
            data={makeOptions}
            searchable
            showLessElements={showLessElements}
            disabled={isLoadingMakes}
            onChange={handleMakeChange}
            value={form.values.make}
            error={form.errors.make}
          />
        </FormField>
        <FormField required>
          <Dropdown
            label={t("car.status")}
            showLessElements={showLessElements}
            placeholder={t("components.CreateCarForm.placeholders.status")}
            data={[
              { value: "owned", label: t("car.statuses.owned") },
              { value: "sold", label: t("car.statuses.sold") },
              { value: "traded", label: t("car.statuses.traded") },
              { value: "totaled", label: t("car.statuses.totaled") },
            ]}
            {...form.getInputProps("status")}
          />
        </FormField>
      </WrapperForFields>

      <Group justify="space-between" mt="2em">
        <Text c="var(--mantine-color-primaryDark-5)" fw={500} fz="sm">
          {t("common.steps", {
            currentStep: activeStep + 1,
            totalSteps: 4,
          })}
        </Text>

        <Flex justify="end" gap="md">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              aria-label={t("forms.cancelButton")}
              c="dark"
              miw={100}
            >
              {t("forms.cancelButton")}
            </Button>
          )}

          <Button
            type="button"
            onClick={nextStep}
            disabled={!garageId || !isStepValid(0)}
            aria-label={t("forms.next")}
            miw={100}
          >
            {t("forms.next")}
          </Button>
        </Flex>
      </Group>
    </Stack>
  );
};

export default Step1;
